import {
  PROXY,
  YES_NO,
  applicantSection,
  feeInfo,
  otherAttachments,
  proxyAttachments,
  replySection,
} from "modules/ebiForms/common";
import { otherPersonFields } from "modules/ebiForms/definitions/sharedFamilyLabour";
import { EbiCondition, EEbiFormCategory, EEbiFormOffice, IEbiFormDefinition } from "modules/ebiForms/typings";

/**
 * H1. Wniosek o dofinansowanie ze środków PFRON: turnus rehabilitacyjny, przedmioty ortopedyczne i środki pomocnicze,
 * sprzęt rehabilitacyjny, likwidacja barier — art. 35a ust. 1 pkt 7 lit. a, c i d ustawy o rehabilitacji; treść wniosku
 * i załączniki: § 11 rozporządzenia MPiPS z 25.06.2002 r. w sprawie zadań powiatu finansowanych z PFRON (tekst
 * z api.sejm.gov.pl/eli/acts/DU/2015/926 pobrany 2026-10-22); turnusy — rozporządzenie MPiPS z 15.11.2007 r.
 * Układ części „turnus” jak wzór PCPR Opole 2026 (SOW) — https://pcpropole.pl/wp-content/uploads/niepelnosprawni/2026-01-13/Wniosek%20Turnus%20Rehabilitacyjny(P)-37_SOW%202026.pdf
 * (pobrany 2026-10-22); pozostałe zadania wg § 11 ust. 1–4 (wzory ujednolica PFRON w SOW). Rodzaj i stopień orzeczenia nie
 * są pytaniami (minimalizacja danych o zdrowiu) — PCPR odczyta je z dołączonej kopii orzeczenia. Załączniki do sprzętu
 * i barier oznaczone w katalogu jako praktyka urzędów — nieobowiązkowe, do potwierdzenia z PCPR.
 */
const TURNUS: EbiCondition = { field: "zadanie", equals: "turnus" };
const ORTHO: EbiCondition = { field: "zadanie", equals: "przedmiotyOrtopedyczne" };
const EQUIPMENT: EbiCondition = { field: "zadanie", equals: "sprzet" };
const BARRIERS: EbiCondition = { field: "zadanie", equals: "bariery" };
/** Zadania z umową o dofinansowanie — wniosek zawiera też dane z § 11 ust. 1 pkt 5–7, 11 i 12. */
const CONTRACT: EbiCondition = { field: "zadanie", in: ["sprzet", "bariery"] };
const ARCHITECTURAL: EbiCondition = { all: [BARRIERS, { field: "rodzajBariery", equals: "architektoniczne" }] };
const OTHER_PERSON: EbiCondition = { field: "wImieniu", in: ["rodzic", "opiekun", "pelnomocnik"] };

const H1: IEbiFormDefinition = {
  code: "H1",
  version: 1,
  title: "Wniosek o dofinansowanie ze środków PFRON (turnus, przedmioty ortopedyczne, sprzęt rehabilitacyjny, likwidacja barier)",
  category: EEbiFormCategory.Disability,
  office: EEbiFormOffice.CountyUnit,
  description:
    "Dofinansowanie ze środków PFRON uczestnictwa w turnusie rehabilitacyjnym, zaopatrzenia w przedmioty ortopedyczne, środki pomocnicze i sprzęt rehabilitacyjny oraz likwidacji barier.",
  keywords: [
    "PFRON",
    "turnus rehabilitacyjny",
    "przedmioty ortopedyczne",
    "środki pomocnicze",
    "sprzęt rehabilitacyjny",
    "bariery architektoniczne",
    "dofinansowanie",
    "niepełnosprawność",
    "PCPR",
  ],
  organ: "Starosta — Powiatowe Centrum Pomocy Rodzinie (w mieście na prawach powiatu: MOPS / MOPR) właściwe według miejsca zamieszkania osoby niepełnosprawnej",
  legalBasis:
    "art. 35a ust. 1 pkt 7 lit. a, c i d ustawy z dnia 27 sierpnia 1997 r. o rehabilitacji zawodowej i społecznej oraz zatrudnianiu osób niepełnosprawnych; § 2, § 5–6 i § 10–13 rozporządzenia Ministra Pracy i Polityki Społecznej z dnia 25 czerwca 2002 r. w sprawie określenia rodzajów zadań powiatu, które mogą być finansowane ze środków Państwowego Funduszu Rehabilitacji Osób Niepełnosprawnych; rozporządzenie Ministra Pracy i Polityki Społecznej z dnia 15 listopada 2007 r. w sprawie turnusów rehabilitacyjnych",
  request: "Wnoszę o dofinansowanie ze środków Państwowego Funduszu Rehabilitacji Osób Niepełnosprawnych zadania wskazanego we wniosku.",
  feeLabel: "bez opłaty",
  fee: "Bez opłaty; pełnomocnictwo — 17 zł.",
  deadlineLabel: "do 30 dni",
  deadline:
    "Przedmioty ortopedyczne, sprzęt rehabilitacyjny i likwidacja barier — do 30 dni od złożenia kompletnego wniosku (§ 12 ust. 3a rozporządzenia); turnus — według rozporządzenia w sprawie turnusów rehabilitacyjnych.",
  variantField: "zadanie",
  variants: {
    turnus: { title: "Wniosek o dofinansowanie ze środków PFRON uczestnictwa w turnusie rehabilitacyjnym" },
    przedmiotyOrtopedyczne: { title: "Wniosek o dofinansowanie ze środków PFRON zaopatrzenia w przedmioty ortopedyczne i środki pomocnicze" },
    sprzet: { title: "Wniosek o dofinansowanie ze środków PFRON zaopatrzenia w sprzęt rehabilitacyjny" },
    bariery: { title: "Wniosek o dofinansowanie ze środków PFRON likwidacji barier architektonicznych, w komunikowaniu się lub technicznych" },
  },
  sections: [
    {
      code: "rodzajDofinansowania",
      title: "Rodzaj dofinansowania",
      fields: [
        feeInfo(
          "infoSow",
          "Wniosek możesz złożyć także w Systemie Obsługi Wsparcia PFRON (SOW) — https://sow.pfron.gov.pl/ (logowanie profilem zaufanym). To kanał preferowany przez PFRON: dane trafiają od razu do PCPR, a status wniosku widać w systemie. Przez Elektroniczne Biuro Interesanta wniosek trafi do PCPR jako przesyłka.",
        ),
        {
          code: "zadanie",
          type: "radio",
          label: "Wnoszę o dofinansowanie",
          required: true,
          options: [
            { value: "turnus", label: "uczestnictwa w turnusie rehabilitacyjnym" },
            { value: "przedmiotyOrtopedyczne", label: "zaopatrzenia w przedmioty ortopedyczne i środki pomocnicze (na zlecenie, na podstawie odrębnych przepisów)" },
            { value: "sprzet", label: "zaopatrzenia w sprzęt rehabilitacyjny (rehabilitacja w warunkach domowych)" },
            { value: "bariery", label: "likwidacji barier architektonicznych, w komunikowaniu się lub technicznych" },
          ],
        },
        {
          code: "rodzajBariery",
          type: "radio",
          label: "Rodzaj barier",
          required: true,
          visibleWhen: BARRIERS,
          hint: "Dofinansowanie przysługuje oddzielnie na każdy rodzaj zadania — na każdy rodzaj barier złóż osobny wniosek (§ 9 ust. 2 rozporządzenia).",
          options: [
            { value: "architektoniczne", label: "bariery architektoniczne (osoba ma trudności w poruszaniu się)" },
            { value: "komunikowanie", label: "bariery w komunikowaniu się" },
            { value: "techniczne", label: "bariery techniczne" },
          ],
        },
      ],
    },
    applicantSection(),
    {
      code: "osobaNiepelnosprawna",
      title: "Osoba niepełnosprawna, której dotyczy wniosek",
      fields: [
        {
          code: "wImieniu",
          type: "radio",
          label: "Składam wniosek",
          required: true,
          defaultValue: "wlasnym",
          options: [
            { value: "wlasnym", label: "we własnym imieniu — jestem osobą niepełnosprawną" },
            { value: "rodzic", label: "jako rodzic (przedstawiciel ustawowy) małoletniej osoby niepełnosprawnej" },
            { value: "opiekun", label: "jako opiekun prawny lub kurator osoby niepełnosprawnej ustanowiony przez sąd" },
            { value: "pelnomocnik", label: "jako pełnomocnik osoby niepełnosprawnej" },
          ],
        },
        {
          code: "pelnomocnictwoZwolnione",
          type: "checkbox",
          label:
            "Jestem małżonkiem, wstępnym, zstępnym lub rodzeństwem mocodawcy — pełnomocnictwo jest zwolnione z opłaty skarbowej",
          visibleWhen: PROXY,
        },
        ...otherPersonFields({
          prefix: "osoba",
          who: "osoby niepełnosprawnej",
          visibleWhen: OTHER_PERSON,
          addressLabel: "Adres zamieszkania osoby niepełnosprawnej",
          sameAddressLabel: "Osoba niepełnosprawna mieszka pod moim adresem zamieszkania",
        }),
      ],
    },
    {
      code: "przedmiotDofinansowania",
      title: "Przedmiot dofinansowania",
      fields: [
        {
          code: "terminTurnusu",
          type: "text",
          label: "Planowany termin uczestnictwa w turnusie rehabilitacyjnym",
          required: true,
          visibleWhen: TURNUS,
          maxLength: 100,
          placeholder: "np. lipiec 2027 r.",
        },
        {
          code: "turnusWczesniej",
          type: "radio",
          label: "Czy osoba niepełnosprawna korzystała już z dofinansowania uczestnictwa w turnusie rehabilitacyjnym ze środków PFRON?",
          required: true,
          visibleWhen: TURNUS,
          options: YES_NO,
        },
        {
          code: "turnusRok",
          type: "text",
          label: "W którym roku",
          required: true,
          visibleWhen: { all: [TURNUS, { field: "turnusWczesniej", equals: "tak" }] },
          width: "third",
          maxLength: 20,
        },
        {
          code: "zakladPracyChronionej",
          type: "radio",
          label: "Czy osoba niepełnosprawna jest zatrudniona w zakładzie pracy chronionej?",
          required: true,
          visibleWhen: TURNUS,
          options: YES_NO,
        },
        {
          code: "opiekunTurnus",
          type: "radio",
          label: "Czy lekarz we wniosku o skierowanie na turnus uznał konieczność pobytu opiekuna?",
          required: true,
          visibleWhen: TURNUS,
          options: YES_NO,
        },
        {
          code: "opiekunImieNazwisko",
          type: "text",
          label: "Imię i nazwisko opiekuna na turnusie",
          required: true,
          visibleWhen: { all: [TURNUS, { field: "opiekunTurnus", equals: "tak" }] },
          maxLength: 200,
        },
        {
          code: "przedmiot",
          type: "text",
          label: "Przedmiot dofinansowania (nazwa przedmiotu, środka pomocniczego, sprzętu albo zakres prac)",
          required: true,
          visibleWhen: { any: [ORTHO, CONTRACT] },
          maxLength: 500,
        },
        {
          code: "etapZlecenia",
          type: "radio",
          label: "Zlecenie na zaopatrzenie",
          required: true,
          visibleWhen: ORTHO,
          options: [
            { value: "zrealizowane", label: "zostało zrealizowane — dołączam fakturę i potwierdzoną kopię zrealizowanego zlecenia" },
            { value: "oferta", label: "nie zostało jeszcze zrealizowane — dołączam kopię zlecenia i ofertę z ceną" },
          ],
        },
        {
          code: "tytulLokalu",
          type: "radio",
          label: "Prawo osoby niepełnosprawnej do nieruchomości",
          required: true,
          visibleWhen: ARCHITECTURAL,
          options: [
            { value: "wlasnosc", label: "jest właścicielem albo użytkownikiem wieczystym nieruchomości" },
            { value: "zgoda", label: "ma zgodę właściciela lokalu lub budynku mieszkalnego, w którym stale zamieszkuje" },
          ],
        },
        {
          code: "celDofinansowania",
          type: "textarea",
          label: "Miejsce realizacji zadania i cel dofinansowania",
          required: true,
          visibleWhen: CONTRACT,
          maxLength: 2000,
        },
        {
          code: "terminRealizacji",
          type: "text",
          label: "Termin rozpoczęcia i przewidywany czas realizacji zadania",
          required: true,
          visibleWhen: BARRIERS,
          maxLength: 200,
        },
        {
          code: "kosztZadania",
          type: "number",
          label: "Przewidywany koszt realizacji zadania [zł]",
          required: true,
          visibleWhen: CONTRACT,
          width: "half",
          decimals: 2,
          min: 0,
        },
        {
          code: "kwotaWnioskowana",
          type: "number",
          label: "Wnioskowana kwota dofinansowania ze środków PFRON [zł]",
          required: true,
          visibleWhen: CONTRACT,
          width: "half",
          decimals: 2,
          min: 0,
          hint: "Sprzęt rehabilitacyjny: do 80% kosztów, nie więcej niż 5-krotność przeciętnego wynagrodzenia; likwidacja barier: do 95% kosztów, nie więcej niż 15-krotność (§ 13 rozporządzenia).",
        },
        {
          code: "nakladyPoniesione",
          type: "textarea",
          label: "Wartość nakładów dotychczas poniesionych na realizację zadania i źródła ich finansowania",
          required: true,
          visibleWhen: BARRIERS,
          maxLength: 1000,
          placeholder: "np. 0 zł",
        },
        {
          code: "inneZrodla",
          type: "textarea",
          label: "Inne źródła finansowania zadania",
          required: true,
          visibleWhen: BARRIERS,
          maxLength: 1000,
          placeholder: "np. brak",
        },
        {
          code: "pfronWczesniej",
          type: "radio",
          label: "Czy osoba niepełnosprawna otrzymała wcześniej dofinansowanie ze środków PFRON?",
          required: true,
          visibleWhen: CONTRACT,
          options: YES_NO,
        },
        {
          code: "pfronWczesniejOpis",
          type: "textarea",
          label: "Przyznane wcześniej środki PFRON — numer umowy, cel, data przyznania i stan rozliczenia",
          required: true,
          visibleWhen: { all: [CONTRACT, { field: "pfronWczesniej", equals: "tak" }] },
          maxLength: 2000,
        },
        { code: "bank", type: "text", label: "Nazwa banku", required: true, visibleWhen: CONTRACT, width: "half", maxLength: 200 },
        {
          code: "rachunek",
          type: "text",
          label: "Numer rachunku bankowego",
          required: true,
          visibleWhen: CONTRACT,
          width: "half",
          maxLength: 40,
          placeholder: "00 0000 0000 0000 0000 0000 0000",
        },
      ],
    },
    {
      code: "dochody",
      title: "Oświadczenie o dochodach",
      fields: [
        { code: "liczbaOsob", type: "number", label: "Liczba osób we wspólnym gospodarstwie domowym", required: true, width: "half", min: 1, max: 30 },
        {
          code: "dochodNaOsobe",
          type: "number",
          label: "Przeciętny miesięczny dochód na osobę [zł]",
          required: true,
          width: "half",
          decimals: 2,
          min: 0,
          hint: "Dochód w rozumieniu przepisów o świadczeniach rodzinnych, podzielony przez liczbę osób we wspólnym gospodarstwie domowym, obliczony za kwartał poprzedzający miesiąc złożenia wniosku.",
        },
        {
          code: "oswiadczenieDochody",
          type: "declaration",
          label:
            "Oświadczam, że przeciętny miesięczny dochód, w rozumieniu przepisów o świadczeniach rodzinnych, podzielony przez liczbę osób we wspólnym gospodarstwie domowym, obliczony za kwartał poprzedzający miesiąc złożenia wniosku, oraz liczba osób we wspólnym gospodarstwie domowym są takie, jak podano powyżej. Oświadczam, że powyższe dane są zgodne z prawdą oraz że jestem świadoma/świadomy odpowiedzialności karnej za składanie nieprawdziwych danych.",
          required: true,
        },
        {
          code: "zaswiadczenieNaTurnus",
          type: "declaration",
          label:
            "Zobowiązuję się do przedstawienia lekarzowi na turnusie rehabilitacyjnym aktualnego zaświadczenia o stanie zdrowia (dotyczy turnusu, którego program przewiduje zabiegi fizjoterapeutyczne).",
          visibleWhen: TURNUS,
        },
      ],
    },
    replySection(),
  ],
  attachments: [
    {
      code: "orzeczenie",
      label: "Kopia orzeczenia o niepełnosprawności, o stopniu niepełnosprawności albo orzeczenia równoważnego (lub kopia wypisu z treści orzeczenia)",
      hint: "Orzeczenie zespołu do spraw orzekania o niepełnosprawności albo ZUS / KRUS (§ 11 ust. 4 pkt 1 rozporządzenia).",
      required: true,
    },
    {
      code: "wniosekLekarza",
      label: "Wniosek lekarza o skierowanie na turnus rehabilitacyjny",
      hint: "Druk wystawiony przez lekarza prowadzącego — PCPR może poprosić o dostarczenie oryginału.",
      visibleWhen: TURNUS,
      required: true,
    },
    {
      code: "oswiadczenieOpiekuna",
      label: "Oświadczenie opiekuna osoby niepełnosprawnej na turnusie",
      hint: "Część PCPR wymaga go, gdy wnioskowane jest dofinansowanie pobytu opiekuna.",
      visibleWhen: { all: [TURNUS, { field: "opiekunTurnus", equals: "tak" }] },
    },
    {
      code: "fakturaZlecenie",
      label: "Faktura z wyodrębnioną kwotą finansowaną przez NFZ i udziałem własnym (albo inny dokument zakupu) wraz z potwierdzoną kopią zrealizowanego zlecenia",
      hint: "§ 11 ust. 4 pkt 2 lit. a rozporządzenia — kopię zlecenia potwierdza za zgodność świadczeniodawca realizujący zlecenie.",
      visibleWhen: { all: [ORTHO, { field: "etapZlecenia", equals: "zrealizowane" }] },
      required: true,
    },
    {
      code: "zlecenieOferta",
      label: "Kopia zlecenia na zaopatrzenie wraz z ofertą (fakturą pro forma)",
      hint: "Oferta z ceną nabycia, kwotą finansowaną przez NFZ, udziałem własnym i terminem realizacji zlecenia (§ 11 ust. 4 pkt 2 lit. b rozporządzenia).",
      visibleWhen: { all: [ORTHO, { field: "etapZlecenia", equals: "oferta" }] },
      required: true,
    },
    {
      code: "zaswiadczenieSprzet",
      label: "Zaświadczenie lekarskie o potrzebie rehabilitacji w warunkach domowych przy użyciu sprzętu",
      hint: "Praktyka PCPR — potwierdź z jednostką, czy jest wymagane.",
      visibleWhen: EQUIPMENT,
    },
    {
      code: "ofertaSprzet",
      label: "Oferta cenowa albo faktura pro forma sprzętu rehabilitacyjnego",
      hint: "Praktyka PCPR.",
      visibleWhen: EQUIPMENT,
    },
    {
      code: "zaswiadczenieBariery",
      label: "Zaświadczenie lekarskie o trudnościach w poruszaniu się albo w komunikowaniu się",
      hint: "Praktyka PCPR — potwierdź z jednostką, czy jest wymagane.",
      visibleWhen: BARRIERS,
    },
    {
      code: "kosztorys",
      label: "Kosztorys albo oferta wykonawcy",
      hint: "Praktyka PCPR.",
      visibleWhen: BARRIERS,
    },
    {
      code: "tytulPrawny",
      label: "Dokument potwierdzający tytuł prawny do lokalu lub budynku",
      hint: "Praktyka PCPR (np. odpis z księgi wieczystej, umowa najmu).",
      visibleWhen: ARCHITECTURAL,
    },
    {
      code: "zgodaWlasciciela",
      label: "Zgoda właściciela lokalu lub budynku mieszkalnego na wykonanie prac",
      hint: "Warunek dofinansowania likwidacji barier architektonicznych, gdy osoba nie jest właścicielem ani użytkownikiem wieczystym (§ 6 pkt 1 rozporządzenia).",
      visibleWhen: { all: [ARCHITECTURAL, { field: "tytulLokalu", equals: "zgoda" }] },
      required: true,
    },
    {
      code: "dokumentacjaBariery",
      label: "Dokumentacja fotograficzna oraz szkic lub projekt planowanych prac",
      hint: "Praktyka PCPR.",
      visibleWhen: BARRIERS,
      multiple: true,
    },
    {
      code: "postanowienieSadu",
      label: "Postanowienie sądu o ustanowieniu opieki lub kurateli",
      hint: "Kopia postanowienia.",
      visibleWhen: { field: "wImieniu", equals: "opiekun" },
      required: true,
    },
    ...proxyAttachments(),
    otherAttachments(),
  ],
};

export default H1;
